import { omit } from 'lodash-es';
import { z } from 'zod';

import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { currencySchema } from '~/features/internationalization/models/currency';

export enum PlanSavingsBlurb {
  Annually = 'Save 20%',
  FourMonth = 'Save 15%',
}

type PlanType = 'individual' | 'team' | 'team-pro';

const braavosPlanPriceSchema = z
  .object({
    plan_length: z.enum(['month', 'year', '4month']),
    list_amount: z.number(),
    list_amount_display: z.string(),
    payable_amount: z.number(),
    payable_amount_display: z.string(),
    payable_amount_per_month: z.number(),
    payable_amount_per_month_display: z.string(),
    prepaid_list_amount: z.number(),
    prepaid_list_amount_display: z.string(),
    prepaid_list_amount_per_month: z.number(),
    prepaid_list_amount_per_month_display: z.string(),
    sku_type: z.enum(['all_access', 'teams_core', 'teams_pro', 'learning_plan']),
    coupon_applied: z.boolean().optional(),
    coupon_expires_at: z.string().optional().nullable(),
    coupon_percent_off: z.number().optional().nullable(),
    plan_length_months: z.number(),
  })
  .transform((value) => {
    let planType: PlanType;
    let displayPriceUnits: string;
    if (value.sku_type === 'all_access') {
      planType = 'individual';
      if (value.plan_length === '4month') {
        displayPriceUnits = ''; // Note: displayPriceUnits are not used on the front-end for 4-month plan.
      } else {
        displayPriceUnits = value.plan_length === 'month' ? '/month' : '/year';
      }
    } else if (value.sku_type === 'teams_core') {
      planType = 'team';
      displayPriceUnits = `/user per ${value.plan_length}`;
    } else {
      planType = 'team-pro';
      displayPriceUnits = `/user per ${value.plan_length}`;
    }

    const billingFrequency = billingFrequencySchema.parse(
      value.plan_length_months === 4
        ? billingFrequencySchema.enum['4month']
        : value.plan_length === 'year'
          ? billingFrequencySchema.enum.annually
          : billingFrequencySchema.enum.monthly
    );

    const savingsBlurb =
      billingFrequency === billingFrequencySchema.enum.annually
        ? PlanSavingsBlurb.Annually
        : billingFrequency === billingFrequencySchema.enum['4month']
          ? PlanSavingsBlurb.FourMonth
          : null;

    return {
      savingsBlurb,
      billingFrequency: billingFrequency as BillingFrequency,
      displayPrice: value.payable_amount_display,
      displayPriceUnits,
      displayPricePerMonth: value.payable_amount_per_month_display,
      planType,
      priceAmount: value.list_amount,
      strikeThroughDisplayPrice: value.payable_amount < value.list_amount ? value.list_amount_display : null,
      strikeThroughDisplayPricePerMonth:
        value.payable_amount_per_month < value.prepaid_list_amount_per_month
          ? value.prepaid_list_amount_per_month_display
          : null,
      couponApplied: Boolean(value.coupon_applied),
      couponExpiresAt: value.coupon_expires_at,
      couponPercentOff: value.coupon_percent_off,
    };
  });

export type BraavosPlanPrice = z.infer<typeof braavosPlanPriceSchema>;
export type BraavosPlanPriceResponse = z.input<typeof braavosPlanPriceSchema>;

export const braavosPlanPricesSchema = z
  .object({
    currency: currencySchema,
    prices: z.array(braavosPlanPriceSchema),
  })
  .transform((value) => {
    return {
      ...value,
      prices: value.prices.reduce(
        (acc, next) => {
          return {
            ...acc,
            [next.billingFrequency]: {
              ...acc[next.billingFrequency],
              [next.planType]: omit(next, ['planType', 'billingFrequency'] as const),
            },
          };
        },
        {} as Record<BillingFrequency, Record<PlanType, Omit<BraavosPlanPrice, 'planType' | 'billingFrequency'>>>
      ),
    };
  });

export type BraavosPlanPrices = z.infer<typeof braavosPlanPricesSchema>;
export type BraavosPlanPricesResponse = z.input<typeof braavosPlanPricesSchema>;
